// src/components/Tutorial/TutorialMain.js
import React, { useState, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Step,
  Stepper,
  StepLabel,
  IconButton,
  Backdrop,
  Fade,
  Slide,
  useTheme,
  useMediaQuery,
  Zoom
} from '@mui/material';
import { Close as CloseIcon, NavigateNext, NavigateBefore } from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { getTutorialSteps } from './tutorialSteps';
import MiniNavigation from './MiniNavigation';
import {
  SubscriptionsList,
  ReviewForm,
  ReferralDashboard,
  ProfileDashboard,
  ReviewsPage,
  CreatorsBrowser,
  VipCard,
  LeaderboardCard,
  SupportCard,
  VerificationCard
} from './Examples';
import './Tutorial.css';

export default function Tutorial({ open, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const { authData, markTutorialCompleted } = useContext(AuthContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const tutorialSteps = getTutorialSteps(isMobile);

  const currentStepData = tutorialSteps[currentStep];

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    markTutorialCompleted(); // This now works in both dev and production
    onClose();
  };

  const handleSkip = () => {
    markTutorialCompleted();
    onClose();
  };

  return (
    <>
      {/* Backdrop */}
      <Backdrop
        className="tutorial-backdrop"
        open={open}
      />

      {/* Tutorial Dialog with animations */}
      <Dialog
        open={open}
        onClose={handleSkip}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ 
          direction: 'up',
          timeout: 400
        }}
        className="tutorial-dialog"
        sx={{
          '& .MuiDialog-paper': {
            className: 'tutorial-dialog-paper'
          }
        }}
      >
        <DialogTitle className="tutorial-dialog-title">
          {/* Animated background elements */}
          <div className="tutorial-title-bg-element" />
          
          <Zoom in={true} timeout={600}>
            <div className="tutorial-title-content">
              <div className="tutorial-title-icon">
                🎯
              </div>
              <Typography variant="h5" component="div" className="tutorial-title-text">
                {currentStepData.title}
              </Typography>
            </div>
          </Zoom>
          <IconButton
            aria-label="close"
            onClick={handleSkip}
            className="tutorial-close-button"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {/* Progress Stepper */}
          <Stepper 
            activeStep={currentStep} 
            alternativeLabel 
            className="tutorial-stepper"
            sx={{ 
              '& .MuiStepLabel-label': {
                className: 'tutorial-stepper-label'
              },
              '& .MuiStepIcon-root': {
                className: 'tutorial-stepper-icon'
              }
            }}
          >
            {tutorialSteps.map((step, index) => (
              <Step key={index}>
                <StepLabel></StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step Content with animation */}
          <Fade in={true} timeout={500} key={currentStep}>
            <Box>
              <Typography 
                variant="body1" 
                className="tutorial-step-content"
              >
                {currentStepData.content}
              </Typography>

              {/* Mini Navigation Example */}
              {currentStepData.showMiniNav && (
                <MiniNavigation 
                  isMobile={currentStepData.isMobile}
                  highlightSection={currentStepData.highlightSection}
                />
              )}

              {/* Profile Dashboard */}
              {currentStepData.showProfileExample && <ProfileDashboard />}

              {/* Subscriptions List */}
              {currentStepData.showSubscriptionExample && <SubscriptionsList />}

              {/* Review Form */}
              {currentStepData.showReviewExample && <ReviewForm />}

              {/* Reviews Page */}
              {currentStepData.showReviewsPageExample && <ReviewsPage />}

              {/* Referral Dashboard */}
              {currentStepData.showReferralExample && <ReferralDashboard authData={authData} />}

              {/* Creators Browser */}
              {currentStepData.showCreatorsExample && <CreatorsBrowser isMobile={isMobile} />}
              
              {/* VIP Card */}
              {currentStepData.showVipExample && <VipCard />}
              
              {/* Leaderboard Card */}
              {currentStepData.showLeaderboardExample && <LeaderboardCard />}
              
              {/* Support Card */}
              {currentStepData.showSupportExample && <SupportCard />}
              
              {/* Verification Card */}
              {currentStepData.showVerificationExample && <VerificationCard />}
            </Box>
          </Fade>

          {/* Step counter */}
          <div className="tutorial-step-counter">
            <Typography variant="caption" color="text.secondary">
              Step {currentStep + 1} of {tutorialSteps.length}
            </Typography>
          </div>
        </DialogContent>

        <DialogActions className="tutorial-dialog-actions">
          <Button 
            onClick={handleSkip} 
            color="inherit"
            className="tutorial-skip-button"
          >
            Skip Tutorial
          </Button>
          
          <div className="tutorial-button-group-row">
            <Button 
              onClick={handlePrevious} 
              disabled={currentStep === 0}
              startIcon={<NavigateBefore />}
              variant="outlined"
              className="tutorial-back-button"
            >
              Back
            </Button>
            <Button 
              onClick={handleNext} 
              endIcon={currentStep === tutorialSteps.length - 1 ? null : <NavigateNext />}
              variant="contained"
              className="tutorial-next-button"
            >
              {currentStep === tutorialSteps.length - 1 ? 'Get Started' : 'Next'}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
}
